import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function SitterRegistrationPage() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    country: '',
    city: '',
    street: '',
    phone: '',
    vatRegistered: '',
    profilePhoto: null as File | null,
    introduction: '',
    motivation: '',
    specialties: '',
    experience: {
      ownedDog: false,
      sitterExperience: false,
      walkerExperience: false,
      largeDog: false,
      puppy: false,
      seniorDog: false,
      cat: false,
      multiPet: false,
    },
    experienceDescription: '',
    snsLink: '',
    petPhoto: null as File | null,
    residencyStatus: '',
    agreements: {
      terms: false,
      platform: false,
      liability: false,
      independent: false,
      tax: false,
      insurance: false,
      accuracy: false,
      pending: false,
      noDirectDeal: false,
    },
    acceptedPets: {
      dog: {
        small: false,
        medium: false,
        large: false,
        xlarge: false,
        noFemaleInHeat: false,
      },
      cat: {
        small: false,
        medium: false,
        large: false,
      },
    },
    services: {
      walking: false,
      visitCare: false,
      boarding: false,
      taxi: false,
    },
    hasCar: '',
    boardingDetails: {
      maxPets: '1',
      homeType: '',
    },
  });

  const [submitted, setSubmitted] = useState(false);
  const [agreementError, setAgreementError] = useState(false);
  const [identityError, setIdentityError] = useState(false);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState<string>('');
  const [petPhotoPreview, setPetPhotoPreview] = useState<string>('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [stripeVerified, setStripeVerified] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeConnectDone, setStripeConnectDone] = useState(false);
  const [stripeConnectLoading, setStripeConnectLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe_connect') === 'success') {
      setStripeConnectDone(true);
      navigate('/sitter-registration', { replace: true });
    }
    if (params.get('stripe_identity') === 'success') {
      setStripeVerified(true);
      setIdentityError(false);
      navigate('/sitter-registration', { replace: true });
    }
  }, [navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('boardingDetails.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        boardingDetails: { ...prev.boardingDetails, [field]: value },
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    if (name.startsWith('experience.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        experience: { ...prev.experience, [field]: checked }
      }));
    } else if (name.startsWith('agreements.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        agreements: { ...prev.agreements, [field]: checked }
      }));
    } else if (name.startsWith('acceptedPets.dog.')) {
      const field = name.split('.')[2];
      setFormData(prev => ({
        ...prev,
        acceptedPets: {
          ...prev.acceptedPets,
          dog: { ...prev.acceptedPets.dog, [field]: checked }
        }
      }));
    } else if (name.startsWith('acceptedPets.cat.')) {
      const field = name.split('.')[2];
      setFormData(prev => ({
        ...prev,
        acceptedPets: {
          ...prev.acceptedPets,
          cat: { ...prev.acceptedPets.cat, [field]: checked }
        }
      }));
    } else if (name.startsWith('services.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        services: { ...prev.services, [field]: checked }
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, field: string) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ ...prev, [field]: file }));
      
      const reader = new FileReader();
      reader.onloadend = () => {
        if (field === 'profilePhoto') {
          setProfilePhotoPreview(reader.result as string);
        } else if (field === 'petPhoto') {
          setPetPhotoPreview(reader.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // バリデーション
    if (!formData.fullName || !formData.email || !formData.city || !formData.introduction || !formData.motivation) {
      alert('必須項目を入力してください');
      return;
    }

    if (!formData.profilePhoto) {
      alert('プロフィール写真をアップロードしてください');
      return;
    }

    if (!stripeVerified) {
      setIdentityError(true);
      const el = document.getElementById('identity-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setIdentityError(false);

    const allAgreementsChecked = Object.values(formData.agreements).every(v => v === true);
    if (!allAgreementsChecked) {
      setAgreementError(true);
      const el = document.getElementById('agreements-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setAgreementError(false);

    // フォームデータを準備
    const submitData = new FormData();
    submitData.append('fullName', formData.fullName);
    submitData.append('email', formData.email);
    submitData.append('country', formData.country);
    submitData.append('city', formData.city);
    submitData.append('street', formData.street);
    submitData.append('phone', formData.phone);
    submitData.append('introduction', formData.introduction);
    submitData.append('motivation', formData.motivation);
    submitData.append('specialties', formData.specialties);
    submitData.append('experience', JSON.stringify(formData.experience));
    submitData.append('experienceDescription', formData.experienceDescription);
    submitData.append('snsLink', formData.snsLink);
    submitData.append('residencyStatus', formData.residencyStatus);
    submitData.append('vatRegistered', formData.vatRegistered === 'yes' ? 'はい（VAT登録あり）' : 'いいえ（VAT登録なし）');
    submitData.append('stripeVerified', stripeVerified ? '本人確認済み' : '未確認');

    if (formData.profilePhoto) {
      submitData.append('profilePhoto', 'アップロード済み');
    }
    if (formData.petPhoto) {
      submitData.append('petPhoto', 'アップロード済み');
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d6s81244k19g20dvs1b0', {
        method: 'POST',
        body: new URLSearchParams(submitData as any),
      });

      if (response.ok) {
        setSubmitted(true);
        window.scrollTo(0, 0);
      } else {
        alert('送信に失敗しました。もう一度お試しください。');
      }
    } catch (error) {
      console.error('送信エラー:', error);
      alert('送信に失敗しました。もう一度お試しください。');
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-[#EEF4F7]">
        {/* ナビゲーション */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#81D8D0] shadow-sm">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="flex items-center justify-between h-24 sm:h-28">
              <Link to="/" className="flex items-center flex-shrink-0">
                <img
                  src="https://static.readdy.ai/image/8518bd20f704ac7d2f3f65f4644eeb9a/37cbdfbab98dc394d9ee285d8ad1648c.svg"
                  alt="PawBridge"
                  className="w-40 h-40 sm:w-48 sm:h-48 object-contain brightness-0"
                />
              </Link>
              <div className="hidden md:flex items-center gap-8 lg:gap-10">
                <Link to="/about" className="relative text-base lg:text-lg font-semibold text-white/90 hover:text-white transition-colors tracking-wide whitespace-nowrap group">
                  PawBridgeについて
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span>
                </Link>
                <Link to="/sitters" className="relative text-base lg:text-lg font-semibold text-white/90 hover:text-white transition-colors tracking-wide whitespace-nowrap group">
                  シッターを探す
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span>
                </Link>
                <Link to="/sitter-registration" className="relative text-base lg:text-lg font-semibold text-white transition-colors tracking-wide whitespace-nowrap">
                  シッター登録
                  <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-white rounded-full"></span>
                </Link>
                <Link to="/login" className="px-6 py-2.5 bg-white text-[#81D8D0] text-base font-bold rounded-full hover:bg-[#1B2A4A] hover:text-white transition-all shadow-md hover:shadow-lg whitespace-nowrap">
                  ログイン
                </Link>
              </div>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
              >
                <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>
            {mobileMenuOpen && (
              <div className="md:hidden pb-4 space-y-1 border-t border-white/20 pt-3">
                <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 rounded-lg transition-colors">PawBridgeについて</Link>
                <Link to="/sitters" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 rounded-lg transition-colors">シッターを探す</Link>
                <Link to="/sitter-registration" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 rounded-lg transition-colors">シッター登録</Link>
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 rounded-lg transition-colors">ログイン</Link>
              </div>
            )}
          </div>
        </nav>

        {/* 送信完了メッセージ */}
        <div className="pt-24 sm:pt-28 max-w-3xl mx-auto px-6 py-20">
          <div className="bg-white rounded-2xl shadow-sm border border-[#D4E8EC] p-12 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-[#81D8D0] to-[#5BC0B8] rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-line text-4xl text-white"></i>
            </div>
            
            <h1 className="text-3xl font-bold text-[#1B2A4A] mb-4">
              シッター応募を受け付けました
            </h1>
            
            <p className="text-[#1B2A4A]/70 text-lg leading-relaxed mb-6">
              ご応募ありがとうございます。現在、提出内容を確認しています。<br />
              審査結果はメールでお知らせします。<br />
              承認後、シッターダッシュボードにアクセスできるようになります。
            </p>

            {/* 本人確認リマインダー */}
            {!stripeVerified && (
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 mb-6 text-left">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-information-line text-amber-500 text-base"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-amber-800 mb-0.5">本人確認が未完了です</p>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    報酬の受け取りには本人確認（Stripe）の完了が必要です。<br />
                    審査通過後、シッターダッシュボードの「本人確認」タブからいつでも再開できます。
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="px-8 py-3 bg-gradient-to-r from-[#81D8D0] to-[#5BC0B8] text-white rounded-full font-medium hover:shadow-lg transition-all whitespace-nowrap"
              >
                ホームへ戻る
              </Link>
              <Link
                to="/sitter-application-status"
                className="px-8 py-3 bg-white border-2 border-[#81D8D0] text-[#81D8D0] rounded-full font-medium hover:bg-[#81D8D0]/5 transition-all whitespace-nowrap"
              >
                マイページを見る
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#EEF4F7]">
      {/* ナビゲーション */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#81D8D0] shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-24 sm:h-28">
            <Link to="/" className="flex items-center flex-shrink-0">
              <img
                src="https://static.readdy.ai/image/8518bd20f704ac7d2f3f65f4644eeb9a/37cbdfbab98dc394d9ee285d8ad1648c.svg"
                alt="PawBridge"
                className="w-40 h-40 sm:w-48 sm:h-48 object-contain brightness-0"
              />
            </Link>
            <div className="hidden md:flex items-center gap-8 lg:gap-10">
              <Link to="/about" className="relative text-base lg:text-lg font-semibold text-white/90 hover:text-white transition-colors tracking-wide whitespace-nowrap group">
                PawBridgeについて
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link to="/sitters" className="relative text-base lg:text-lg font-semibold text-white/90 hover:text-white transition-colors tracking-wide whitespace-nowrap group">
                シッターを探す
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white rounded-full transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link to="/sitter-registration" className="relative text-base lg:text-lg font-semibold text-white transition-colors tracking-wide whitespace-nowrap">
                シッター登録
                <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-white rounded-full"></span>
              </Link>
              <Link to="/login" className="px-6 py-2.5 bg-white text-[#81D8D0] text-base font-bold rounded-full hover:bg-[#1B2A4A] hover:text-white transition-all shadow-md hover:shadow-lg whitespace-nowrap">
                ログイン
              </Link>
            </div>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 space-y-1 border-t border-white/20 pt-3">
              <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 rounded-lg transition-colors">PawBridgeについて</Link>
              <Link to="/sitters" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 rounded-lg transition-colors">シッターを探す</Link>
              <Link to="/sitter-registration" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 rounded-lg transition-colors">シッター登録</Link>
              <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block px-4 py-3 text-sm font-semibold text-white hover:bg-white/10 rounded-lg transition-colors">ログイン</Link>
            </div>
          )}
        </div>
      </nav>

      {/* ヒーローセクション */}
      <section className="pt-32 sm:pt-36 bg-gradient-to-br from-[#D4F0ED] to-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-[#1B2A4A] mb-6">
            PawBridge認定シッターに応募する
          </h1>
          <p className="text-lg text-[#1B2A4A]/70 leading-relaxed max-w-3xl mx-auto">
            海外で暮らす日本人同士が、安心して愛犬を預けられる環境をつくるため、<br />
            PawBridgeではシッター登録に簡単な審査を設けています。
          </p>
        </div>
      </section>

      {/* 応募前の準備 */}
      <section className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-[#D4E8EC] p-8">
          <h2 className="text-2xl font-bold text-[#1B2A4A] mb-6 flex items-center gap-3">
            <i className="ri-file-list-3-line text-[#81D8D0]"></i>
            応募前にご用意ください
          </h2>
          <ul className="space-y-3">
            {[
              'プロフィール写真',
              '本人確認書類（パスポート、在留カード、EU IDのいずれか）',
              '自己紹介文',
              '犬やペットに関する経験',
              '居住都市',
              '銀行口座情報（承認後でも可）'
            ].map((item, index) => (
              <li key={index} className="flex items-start gap-3 text-[#1B2A4A]/80">
                <i className="ri-checkbox-circle-fill text-[#81D8D0] text-xl mt-0.5"></i>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 応募フォーム */}
      <section className="max-w-4xl mx-auto px-6 pb-20">
        <form onSubmit={handleSubmit} className="space-y-8" data-readdy-form id="sitter-application-form">
          
          {/* 基本情報 */}
          <div className="bg-white rounded-2xl shadow-sm border border-[#D4E8EC] p-8">
            <h3 className="text-xl font-bold text-[#1B2A4A] mb-6">基本情報</h3>
            
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#1B2A4A] mb-2">
                  氏名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-[#D4E8EC] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#81D8D0] text-sm"
                  placeholder="山田 太郎"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1B2A4A] mb-2">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-[#D4E8EC] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#81D8D0] text-sm"
                  placeholder="example@email.com"
                  required
                />
              </div>

              {/* 住所エリア */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#1B2A4A] mb-2">
                    国 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-[#D4E8EC] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#81D8D0] text-sm"
                    placeholder="例：ドイツ、フランス、オランダ"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1B2A4A] mb-2">
                    市・地域 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-[#D4E8EC] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#81D8D0] text-sm"
                    placeholder="例：ベルリン"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1B2A4A] mb-2">
                  Street（番地・通り名）
                </label>
                <input
                  type="text"
                  name="street"
                  value={formData.street}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-[#D4E8EC] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#81D8D0] text-sm"
                  placeholder="例：Hauptstraße 12"
                />
              </div>

              {/* VAT登録 */}
              <div>
                <label className="block text-sm font-medium text-[#1B2A4A] mb-1">
                  VAT登録（BTW / Steuernummer）はお持ちですか？
                </label>
                <p className="text-xs text-[#1B2A4A]/55 mb-3">
                  事業者としてVAT（付加価値税）番号が登録されている場合、予約料金にVATが加算されます。
                </p>
                <div className="flex gap-3">
                  {[
                    { value: 'yes', label: 'はい、登録あり', icon: 'ri-checkbox-circle-fill', activeColor: 'border-[#81D8D0] bg-[#D4F0ED] text-[#1B2A4A]', iconColor: 'text-[#5BC0B8]' },
                    { value: 'no', label: 'いいえ、なし', icon: 'ri-close-circle-line', activeColor: 'border-[#D4E8EC] bg-[#EEF4F7] text-[#1B2A4A]', iconColor: 'text-[#1B2A4A]/40' },
                  ].map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex items-center gap-2 px-5 py-3 border-2 rounded-xl cursor-pointer transition-all text-sm font-medium whitespace-nowrap flex-1 justify-center ${
                        formData.vatRegistered === opt.value
                          ? opt.activeColor + ' border-[#81D8D0]'
                          : 'border-[#D4E8EC] text-[#1B2A4A]/60 hover:border-[#81D8D0]/40 hover:bg-[#EEF4F7]/70'
                      }`}
                    >
                      <input
                        type="radio"
                        name="vatRegistered"
                        value={opt.value}
                        checked={formData.vatRegistered === opt.value}
                        onChange={handleInputChange}
                        className="sr-only"
                      />
                      <i className={`${opt.icon} text-base ${formData.vatRegistered === opt.value ? opt.iconColor : 'text-[#1B2A4A]/30'}`}></i>
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1B2A4A] mb-2">
                  電話番号（任意）
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-[#D4E8EC] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#81D8D0] text-sm"
                  placeholder="+49 123 456 7890"
                />
              </div>
            </div>
          </div>

          {/* プロフィール */}
          <div className="bg-white rounded-2xl shadow-sm border border-[#D4E8EC] p-8">
            <h3 className="text-xl font-bold text-[#1B2A4A] mb-6">プロフィール</h3>
            
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#1B2A4A] mb-2">
                  プロフィール写真 <span className="text-red-500">*</span>
                </label>
                <div className="flex items-start gap-4">
                  {profilePhotoPreview && (
                    <img
                      src={profilePhotoPreview}
                      alt="プロフィール"
                      className="w-24 h-24 rounded-full object-cover border-2 border-[#D4E8EC]"
                    />
                  )}
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, 'profilePhoto')}
                      className="w-full px-4 py-3 border border-[#D4E8EC] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#81D8D0] text-sm"
                      required
                    />
                    <p className="text-xs text-[#1B2A4A]/60 mt-2">
                      顔がはっきり見える写真をアップロードしてください
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1B2A4A] mb-2">
                  自己紹介 <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="introduction"
                  value={formData.introduction}
                  onChange={handleInputChange}
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-[#D4E8EC] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#81D8D0] text-sm resize-none"
                  placeholder="あなたの経歴、性格、ペットへの想いなどを教えてください"
                  required
                ></textarea>
                <p className="text-xs text-[#1B2A4A]/60 mt-2">
                  {formData.introduction.length}/500文字
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1B2A4A] mb-2">
                  なぜシッターになりたいですか？ <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="motivation"
                  value={formData.motivation}
                  onChange={handleInputChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-[#D4E8EC] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#81D8D0] text-sm resize-none"
                  placeholder="シッターとして活動したい理由を教えてください"
                  required
                ></textarea>
                <p className="text-xs text-[#1B2A4A]/60 mt-2">
                  {formData.motivation.length}/500文字
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1B2A4A] mb-2">
                  得意な犬種や対応できるサイズ
                </label>
                <textarea
                  name="specialties"
                  value={formData.specialties}
                  onChange={handleInputChange}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-[#D4E8EC] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#81D8D0] text-sm resize-none"
                  placeholder="例：小型犬から大型犬まで対応可能、柴犬やゴールデンレトリバーが得意"
                ></textarea>
                <p className="text-xs text-[#1B2A4A]/60 mt-2">
                  {formData.specialties.length}/500文字
                </p>
              </div>
            </div>
          </div>

          {/* 犬・ペット経験 */}
          <div className="bg-white rounded-2xl shadow-sm border border-[#D4E8EC] p-8">
            <h3 className="text-xl font-bold text-[#1B2A4A] mb-6">犬・ペット経験</h3>
            
            <div className="space-y-5">
              <div>
                <p className="text-sm font-medium text-[#1B2A4A] mb-3">
                  該当する経験にチェックを入れてください
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    { key: 'ownedDog', label: '犬を飼った経験がある' },
                    { key: 'sitterExperience', label: 'ドッグシッター経験がある' },
                    { key: 'walkerExperience', label: 'ドッグウォーカー経験がある' },
                    { key: 'largeDog', label: '大型犬の対応経験がある' },
                    { key: 'puppy', label: '子犬の対応経験がある' },
                    { key: 'seniorDog', label: '高齢犬の対応経験がある' },
                    { key: 'cat', label: '猫の対応経験がある' },
                    { key: 'multiPet', label: '多頭飼いの対応経験がある' },
                  ].map((item) => (
                    <label key={item.key} className="flex items-center gap-3 p-3 border border-[#D4E8EC] rounded-lg hover:bg-[#EEF4F7] transition-colors cursor-pointer">
                      <input
                        type="checkbox"
                        name={`experience.${item.key}`}
                        checked={formData.experience[item.key as keyof typeof formData.experience]}
                        onChange={handleCheckboxChange}
                        className="w-5 h-5 text-[#81D8D0] border-[#D4E8EC] rounded focus:ring-[#81D8D0] mt-0.5 flex-shrink-0"
                      />
                      <span className="text-sm text-[#1B2A4A]">{item.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1B2A4A] mb-2">
                  これまでのペット経験を教えてください
                </label>
                <textarea
                  name="experienceDescription"
                  value={formData.experienceDescription}
                  onChange={handleInputChange}
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-[#D4E8EC] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#81D8D0] text-sm resize-none"
                  placeholder="具体的なエピソードや経験年数などを教えてください"
                ></textarea>
                <p className="text-xs text-[#1B2A4A]/60 mt-2">
                  {formData.experienceDescription.length}/500文字
                </p>
              </div>
            </div>
          </div>

          {/* 提供できるサービス */}
          <div className="bg-white rounded-2xl shadow-sm border border-[#D4E8EC] p-8">
            <h3 className="text-xl font-bold text-[#1B2A4A] mb-2">提供できるサービス</h3>
            <p className="text-sm text-[#1B2A4A]/60 mb-3">提供したいサービスをすべて選択してください（複数選択可）</p>
            <div className="flex items-start gap-2 bg-[#EEF4F7] rounded-lg px-4 py-3 mb-6">
              <i className="ri-information-line text-[#5BC0B8] text-sm mt-0.5 flex-shrink-0"></i>
              <p className="text-xs text-[#1B2A4A]/70 leading-relaxed">
                各サービスの料金はシッターご自身で自由に設定できます。承認後、ダッシュボードから料金を登録してください。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {[
                {
                  key: 'walking',
                  icon: 'ri-walk-line',
                  color: 'text-[#5BC0B8]',
                  bg: 'bg-[#D4F0ED]',
                  activeBorder: 'border-[#81D8D0] bg-[#EEF4F7]',
                  label: '犬の散歩',
                  desc: '信頼できるドッグウォーカーが愛犬をお世話。散歩後には写真と最新情報をお届けします',
                },
                {
                  key: 'visitCare',
                  icon: 'ri-home-heart-line',
                  color: 'text-rose-400',
                  bg: 'bg-rose-50',
                  activeBorder: 'border-rose-300 bg-rose-50',
                  label: '訪問ケア',
                  desc: 'ご自宅でのお世話。餌やり、遊び、トイレ掃除など',
                },
                {
                  key: 'boarding',
                  icon: 'ri-moon-line',
                  color: 'text-indigo-400',
                  bg: 'bg-indigo-50',
                  activeBorder: 'border-indigo-300 bg-indigo-50',
                  label: 'お泊まり',
                  desc: 'シッター宅でのお預かり。24時間安心ケア',
                },
                {
                  key: 'taxi',
                  icon: 'ri-taxi-line',
                  color: 'text-amber-500',
                  bg: 'bg-amber-50',
                  activeBorder: 'border-amber-300 bg-amber-50',
                  label: 'タクシー送迎',
                  desc: '車を持っていない方向けに動物病院などへの送迎サービス',
                },
              ].map((service) => {
                const isChecked = formData.services[service.key as keyof typeof formData.services];
                return (
                  <label
                    key={service.key}
                    className={`flex items-start gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      isChecked
                        ? service.activeBorder
                        : 'border-[#D4E8EC] hover:border-[#81D8D0]/40 hover:bg-[#EEF4F7]/50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      name={`services.${service.key}`}
                      checked={isChecked}
                      onChange={handleCheckboxChange}
                      className="sr-only"
                    />
                    <div className={`w-10 h-10 flex items-center justify-center ${service.bg} rounded-full flex-shrink-0 mt-0.5`}>
                      <i className={`${service.icon} ${service.color} text-lg`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-[#1B2A4A]">{service.label}</span>
                        {isChecked && (
                          <span className="w-5 h-5 flex items-center justify-center bg-[#5BC0B8] rounded-full flex-shrink-0">
                            <i className="ri-check-line text-white text-xs"></i>
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#1B2A4A]/60 leading-relaxed">{service.desc}</p>
                    </div>
                  </label>
                );
              })}
            </div>

            {/* 車の送迎 */}
            <div className="border-t border-[#D4E8EC] pt-5">
              <p className="text-sm font-medium text-[#1B2A4A] mb-3">
                <i className="ri-car-line mr-2 text-[#5BC0B8]"></i>
                車での送迎は可能ですか？
              </p>
              <div className="flex gap-3">
                {[
                  { value: 'yes', label: 'はい、可能です' },
                  { value: 'no', label: 'いいえ、できません' },
                  { value: 'maybe', label: '条件次第で可能' },
                ].map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-center gap-2 px-4 py-2.5 border-2 rounded-full cursor-pointer transition-all text-sm font-medium whitespace-nowrap ${
                      formData.hasCar === opt.value
                        ? 'border-[#81D8D0] bg-[#D4F0ED] text-[#1B2A4A]'
                        : 'border-[#D4E8EC] text-[#1B2A4A]/60 hover:border-[#81D8D0]/40'
                    }`}
                  >
                    <input
                      type="radio"
                      name="hasCar"
                      value={opt.value}
                      checked={formData.hasCar === opt.value}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    {formData.hasCar === opt.value && (
                      <i className="ri-check-line text-[#5BC0B8] text-sm"></i>
                    )}
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>

            {/* お泊まり追加質問（条件表示） */}
            {formData.services.boarding && (
              <div className="mt-5 border-t border-[#D4E8EC] pt-5">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 flex items-center justify-center bg-indigo-50 rounded-full">
                    <i className="ri-moon-line text-indigo-400 text-sm"></i>
                  </div>
                  <p className="text-sm font-semibold text-[#1B2A4A]">お泊まりについて教えてください</p>
                </div>

                <div className="space-y-4">
                  {/* 最大預かり頭数 */}
                  <div>
                    <label className="block text-sm font-medium text-[#1B2A4A] mb-2">
                      同時に預かれる最大頭数
                    </label>
                    <div className="flex gap-2">
                      {['1', '2', '3', '4以上'].map((n) => (
                        <label
                          key={n}
                          className={`flex items-center justify-center w-14 h-12 border-2 rounded-xl cursor-pointer transition-all text-sm font-semibold ${
                            formData.boardingDetails.maxPets === n
                              ? 'border-indigo-300 bg-indigo-50 text-indigo-600'
                              : 'border-[#D4E8EC] text-[#1B2A4A]/50 hover:border-indigo-200'
                          }`}
                        >
                          <input
                            type="radio"
                            name="boardingDetails.maxPets"
                            value={n}
                            checked={formData.boardingDetails.maxPets === n}
                            onChange={handleInputChange}
                            className="sr-only"
                          />
                          {n}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* 住居タイプ */}
                  <div>
                    <label className="block text-sm font-medium text-[#1B2A4A] mb-2">
                      住居タイプ
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {[
                        { value: 'house', label: '一軒家', icon: 'ri-home-4-line' },
                        { value: 'apartment', label: 'アパート・マンション', icon: 'ri-building-line' },
                        { value: 'apartment-garden', label: 'アパート（庭付き）', icon: 'ri-plant-line' },
                        { value: 'studio', label: 'スタジオ', icon: 'ri-door-line' },
                        { value: 'shared', label: 'シェアハウス', icon: 'ri-group-line' },
                        { value: 'other', label: 'その他', icon: 'ri-more-line' },
                      ].map((type) => (
                        <label
                          key={type.value}
                          className={`flex items-center gap-2 p-3 border-2 rounded-xl cursor-pointer transition-all ${
                            formData.boardingDetails.homeType === type.value
                              ? 'border-indigo-300 bg-indigo-50'
                              : 'border-[#D4E8EC] hover:border-indigo-200 hover:bg-indigo-50/30'
                          }`}
                        >
                          <input
                            type="radio"
                            name="boardingDetails.homeType"
                            value={type.value}
                            checked={formData.boardingDetails.homeType === type.value}
                            onChange={handleInputChange}
                            className="sr-only"
                          />
                          <i className={`${type.icon} text-indigo-400 text-base flex-shrink-0`}></i>
                          <span className="text-xs font-medium text-[#1B2A4A]">{type.label}</span>
                          {formData.boardingDetails.homeType === type.value && (
                            <i className="ri-check-line text-indigo-400 text-xs ml-auto"></i>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* 対応できるペットとサイズ */}
          <div className="bg-white rounded-2xl shadow-sm border border-[#D4E8EC] p-8">
            <h3 className="text-xl font-bold text-[#1B2A4A] mb-2">対応できるペットとサイズ</h3>
            <p className="text-sm text-[#1B2A4A]/60 mb-6">お預かりできる動物の種類と体重を選択してください（複数選択可）</p>

            <div className="space-y-6">
              {/* 犬 */}
              <div className="border border-[#D4E8EC] rounded-xl p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#D4F0ED] rounded-full">
                    <i className="ri-heart-fill text-[#5BC0B8] text-lg"></i>
                  </div>
                  <span className="text-base font-semibold text-[#1B2A4A]">犬</span>
                </div>

                <p className="text-xs font-medium text-[#1B2A4A]/60 uppercase tracking-wider mb-3">対応できる体重</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                  {[
                    { key: 'small', label: '0〜7 kg', desc: '小型犬' },
                    { key: 'medium', label: '7〜18 kg', desc: '中型犬' },
                    { key: 'large', label: '18〜45 kg', desc: '大型犬' },
                    { key: 'xlarge', label: '45 kg 以上', desc: '超大型犬' },
                  ].map((size) => (
                    <label
                      key={size.key}
                      className={`flex flex-col items-center gap-1 p-3 border-2 rounded-xl cursor-pointer transition-all ${
                        formData.acceptedPets.dog[size.key as keyof typeof formData.acceptedPets.dog]
                          ? 'border-[#81D8D0] bg-[#D4F0ED]'
                          : 'border-[#D4E8EC] hover:border-[#81D8D0]/50 hover:bg-[#EEF4F7]'
                      }`}
                    >
                      <input
                        type="checkbox"
                        name={`acceptedPets.dog.${size.key}`}
                        checked={!!formData.acceptedPets.dog[size.key as keyof typeof formData.acceptedPets.dog]}
                        onChange={handleCheckboxChange}
                        className="sr-only"
                      />
                      <span className="text-sm font-semibold text-[#1B2A4A]">{size.label}</span>
                      <span className="text-xs text-[#1B2A4A]/50">{size.desc}</span>
                      {formData.acceptedPets.dog[size.key as keyof typeof formData.acceptedPets.dog] && (
                        <i className="ri-check-line text-[#5BC0B8] text-base"></i>
                      )}
                    </label>
                  ))}
                </div>

                <div className="border-t border-[#D4E8EC] pt-4">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <input
                      type="checkbox"
                      name="acceptedPets.dog.noFemaleInHeat"
                      checked={formData.acceptedPets.dog.noFemaleInHeat}
                      onChange={handleCheckboxChange}
                      className="w-5 h-5 text-[#81D8D0] border-[#D4E8EC] rounded focus:ring-[#81D8D0] flex-shrink-0"
                    />
                    <div>
                      <span className="text-sm font-medium text-[#1B2A4A] group-hover:text-[#5BC0B8] transition-colors">発情期のメスは対応不可</span>
                      <p className="text-xs text-[#1B2A4A]/50 mt-0.5">ヒート中のメス犬はお断りする場合にチェック</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* 猫 */}
              <div className="border border-[#D4E8EC] rounded-xl p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#FEF3C7] rounded-full">
                    <i className="ri-star-fill text-amber-400 text-lg"></i>
                  </div>
                  <span className="text-base font-semibold text-[#1B2A4A]">猫</span>
                </div>

                <p className="text-xs font-medium text-[#1B2A4A]/60 uppercase tracking-wider mb-3">対応できる体重</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: 'small', label: '〜5 kg', desc: '成猫・小柄' },
                    { key: 'medium', label: '5〜8 kg', desc: '成猫・標準' },
                    { key: 'large', label: '8 kg 以上', desc: '大型種' },
                  ].map((size) => (
                    <label
                      key={size.key}
                      className={`flex flex-col items-center gap-1 p-3 border-2 rounded-xl cursor-pointer transition-all ${
                        formData.acceptedPets.cat[size.key as keyof typeof formData.acceptedPets.cat]
                          ? 'border-amber-300 bg-amber-50'
                          : 'border-[#D4E8EC] hover:border-amber-200 hover:bg-amber-50/50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        name={`acceptedPets.cat.${size.key}`}
                        checked={!!formData.acceptedPets.cat[size.key as keyof typeof formData.acceptedPets.cat]}
                        onChange={handleCheckboxChange}
                        className="sr-only"
                      />
                      <span className="text-sm font-semibold text-[#1B2A4A]">{size.label}</span>
                      <span className="text-xs text-[#1B2A4A]/50">{size.desc}</span>
                      {formData.acceptedPets.cat[size.key as keyof typeof formData.acceptedPets.cat] && (
                        <i className="ri-check-line text-amber-400 text-base"></i>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* 本人確認 */}
          <div id="identity-section" className={`bg-white rounded-2xl border p-8 transition-all ${identityError && !stripeVerified ? 'border-red-300 bg-red-50/30' : 'border-[#D4E8EC]'}`}>
            {/* ヘッダー */}
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center bg-[#D4F0ED] rounded-full flex-shrink-0">
                <i className="ri-shield-check-line text-[#5BC0B8] text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1B2A4A]">本人確認について</h3>
              <span className="px-2 py-0.5 bg-red-50 border border-red-200 text-red-500 text-xs font-semibold rounded-full whitespace-nowrap">必須</span>
            </div>

            {identityError && !stripeVerified && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5">
                <i className="ri-error-warning-line text-red-500 text-base flex-shrink-0"></i>
                <p className="text-sm text-red-600 font-medium">本人確認を完了してから送信してください</p>
              </div>
            )}

            {/* 説明文 */}
            <p className="text-sm text-[#1B2A4A]/70 leading-relaxed mb-6">
              PawBridgeでは、安心・安全なサービス提供のため、<br />
              本人確認は外部の安全な決済サービス<strong className="text-[#1B2A4A]">Stripe</strong>を通じて行われます。<br /><br />
              パスポートや身分証明書の情報はPawBridgeには保存されず、<br />
              Stripeによって安全に処理されます。
            </p>

            {/* インフォボックス */}
            <div className="bg-[#F0FBF9] border border-[#C4E8E4] rounded-xl px-5 py-4 mb-6 space-y-2.5">
              {[
                '個人情報はPawBridgeに保存されません',
                'Stripeによる安全な本人確認',
                '数分で完了',
              ].map((item) => (
                <div key={item} className="flex items-center gap-2.5">
                  <div className="w-5 h-5 flex items-center justify-center bg-[#5BC0B8] rounded-full flex-shrink-0">
                    <i className="ri-check-line text-white text-xs"></i>
                  </div>
                  <span className="text-sm text-[#1B2A4A] font-medium">{item}</span>
                </div>
              ))}
            </div>

            {/* ボタンエリア */}
            {stripeVerified ? (
              <div className="flex items-center gap-3 bg-[#F0FBF9] border border-[#5BC0B8] rounded-xl px-5 py-4">
                <div className="w-9 h-9 flex items-center justify-center bg-[#5BC0B8] rounded-full flex-shrink-0">
                  <i className="ri-check-line text-white text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-bold text-[#1B2A4A]">本人確認が完了しました</p>
                  <p className="text-xs text-[#1B2A4A]/60 mt-0.5">Stripeによる確認済み</p>
                </div>
              </div>
            ) : (
              <div>
                {/* 遷移前の案内テキスト */}
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-external-link-line text-[#1B2A4A]/30 text-sm flex-shrink-0"></i>
                  <p className="text-xs text-[#1B2A4A]/50 leading-relaxed">
                    ボタンを押すと、<strong className="text-[#1B2A4A]/70">Stripeの安全な認証ページ</strong>に移動します。
                    確認後はPawBridgeに自動的に戻ります。
                  </p>
                </div>

                <button
                  type="button"
                  onClick={async () => {
                    setStripeLoading(true);
                    try {
                      const returnUrl = `${window.location.origin}/sitter-registration?stripe_identity=success`;
                      const res = await fetch(
                        'https://fifljdxudukaysotzido.supabase.co/functions/v1/stripe-identity-verification',
                        {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ return_url: returnUrl }),
                        }
                      );
                      const data = await res.json();
                      if (data.url) {
                        window.location.href = data.url;
                      } else {
                        throw new Error(data.error || '接続に失敗しました');
                      }
                    } catch (err) {
                      console.error(err);
                      alert('Stripe Identity への接続に失敗しました。もう一度お試しください。');
                      setStripeLoading(false);
                    }
                  }}
                  disabled={stripeLoading}
                  className="w-full flex items-center justify-center gap-2.5 py-3.5 bg-[#1B2A4A] text-white text-sm font-semibold rounded-full hover:bg-[#243659] transition-all disabled:opacity-70 cursor-pointer whitespace-nowrap"
                >
                  {stripeLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base"></i>
                      Stripeに接続中...
                    </>
                  ) : (
                    <>
                      <i className="ri-shield-check-line text-base"></i>
                      本人確認を開始する
                      <i className="ri-external-link-line text-xs opacity-60 ml-0.5"></i>
                    </>
                  )}
                </button>

                {/* ボタン下の信頼バッジ */}
                <div className="flex items-center justify-center gap-4 mt-3">
                  <div className="flex items-center gap-1.5 text-[#1B2A4A]/35">
                    <i className="ri-lock-line text-xs"></i>
                    <span className="text-xs">SSL暗号化通信</span>
                  </div>
                  <span className="text-[#1B2A4A]/20 text-xs">|</span>
                  <div className="flex items-center gap-1.5 text-[#1B2A4A]/35">
                    <i className="ri-eye-off-line text-xs"></i>
                    <span className="text-xs">PawBridgeには保存されません</span>
                  </div>
                </div>

                <p className="text-xs text-[#1B2A4A]/40 text-center mt-2">
                  ※報酬受け取りに必要。後からダッシュボードで再開も可能です。
                </p>
              </div>
            )}
          </div>

          {/* 支払い設定（報酬受け取り） */}
          <div className="bg-white rounded-2xl border border-[#D4E8EC] p-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 flex items-center justify-center bg-[#D4F0ED] rounded-full flex-shrink-0">
                <i className="ri-bank-card-line text-[#5BC0B8] text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1B2A4A]">報酬の受け取り設定</h3>
            </div>

            <p className="text-sm text-[#1B2A4A]/70 leading-relaxed mb-5">
              シッターへの報酬は <strong className="text-[#1B2A4A]">Stripe Connect</strong> を通じて安全にお支払いします。<br />
              銀行口座の情報はPawBridgeには保存されず、Stripeが直接管理します。
            </p>

            <div className="bg-[#F0FBF9] border border-[#C4E8E4] rounded-xl px-5 py-4 mb-6 space-y-2.5">
              {[
                '銀行口座情報はStripeが安全に管理',
                '審査通過後、即日から報酬を受け取れます',
                '後からダッシュボードでも設定できます',
              ].map((item) => (
                <div key={item} className="flex items-center gap-2.5">
                  <div className="w-5 h-5 flex items-center justify-center bg-[#5BC0B8] rounded-full flex-shrink-0">
                    <i className="ri-check-line text-white text-xs"></i>
                  </div>
                  <span className="text-sm text-[#1B2A4A] font-medium">{item}</span>
                </div>
              ))}
            </div>

            {stripeConnectDone ? (
              <div className="flex items-center gap-3 bg-[#F0FBF9] border border-[#5BC0B8] rounded-xl px-5 py-4">
                <div className="w-9 h-9 flex items-center justify-center bg-[#5BC0B8] rounded-full flex-shrink-0">
                  <i className="ri-check-line text-white text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-bold text-[#1B2A4A]">支払い設定が完了しました</p>
                  <p className="text-xs text-[#1B2A4A]/60 mt-0.5">Stripe Connect による口座連携済み</p>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-external-link-line text-[#1B2A4A]/30 text-sm flex-shrink-0"></i>
                  <p className="text-xs text-[#1B2A4A]/50 leading-relaxed">
                    ボタンを押すと、<strong className="text-[#1B2A4A]/70">Stripeの安全な設定ページ</strong>に移動します。
                    完了後はPawBridgeに自動的に戻ります。
                  </p>
                </div>

                <button
                  type="button"
                  disabled={stripeConnectLoading}
                  onClick={async () => {
                    setStripeConnectLoading(true);
                    try {
                      const returnUrl = `${window.location.origin}/sitter-registration?stripe_connect=success`;
                      const refreshUrl = `${window.location.origin}/sitter-registration?stripe_connect=refresh`;
                      const res = await fetch(
                        'https://fifljdxudukaysotzido.supabase.co/functions/v1/stripe-connect-onboarding',
                        {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            email: formData.email || undefined,
                            name: formData.fullName || undefined,
                            return_url: returnUrl,
                            refresh_url: refreshUrl,
                          }),
                        }
                      );
                      const data = await res.json();
                      if (data.url) {
                        window.location.href = data.url;
                      } else {
                        throw new Error(data.error || '接続に失敗しました');
                      }
                    } catch (err) {
                      console.error(err);
                      alert('Stripe Connect への接続に失敗しました。もう一度お試しください。');
                      setStripeConnectLoading(false);
                    }
                  }}
                  className="w-full flex items-center justify-center gap-2.5 py-3.5 bg-[#1B2A4A] text-white text-sm font-semibold rounded-full hover:bg-[#243659] transition-all disabled:opacity-70 cursor-pointer whitespace-nowrap"
                >
                  {stripeConnectLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-base"></i>
                      Stripeに接続中...
                    </>
                  ) : (
                    <>
                      <i className="ri-bank-card-line text-base"></i>
                      報酬受け取り口座を登録する
                      <i className="ri-external-link-line text-xs opacity-60 ml-0.5"></i>
                    </>
                  )}
                </button>

                <div className="flex items-center justify-center gap-4 mt-3">
                  <div className="flex items-center gap-1.5 text-[#1B2A4A]/35">
                    <i className="ri-lock-line text-xs"></i>
                    <span className="text-xs">SSL暗号化通信</span>
                  </div>
                  <span className="text-[#1B2A4A]/20 text-xs">|</span>
                  <div className="flex items-center gap-1.5 text-[#1B2A4A]/35">
                    <i className="ri-eye-off-line text-xs"></i>
                    <span className="text-xs">口座情報はPawBridgeに保存されません</span>
                  </div>
                </div>

                <p className="text-xs text-[#1B2A4A]/40 text-center mt-2">
                  ※後からシッターダッシュボードでも設定できます
                </p>
              </div>
            )}
          </div>

          {/* 同意事項 */}
          <div id="agreements-section" className={`bg-white rounded-2xl shadow-sm border p-8 transition-all ${agreementError ? 'border-red-300 bg-red-50/30' : 'border-[#D4E8EC]'}`}>
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-xl font-bold text-[#1B2A4A]">同意事項</h3>
              <span className="px-2 py-0.5 bg-red-50 border border-red-200 text-red-500 text-xs font-semibold rounded-full whitespace-nowrap">必須</span>
            </div>
            <p className="text-sm text-[#1B2A4A]/60 mb-6">以下のすべての項目にチェックを入れてください。未チェックがある場合は送信できません。</p>
            {agreementError && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5">
                <i className="ri-error-warning-line text-red-500 text-base flex-shrink-0"></i>
                <p className="text-sm text-red-600 font-medium">すべての同意項目にチェックを入れてください</p>
              </div>
            )}
            
            <div className="space-y-4">
              {[
                {
                  key: 'terms',
                  label: (
                    <span>
                      PawBridgeの
                      <a
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#81D8D0] underline hover:text-[#5bc0b8] transition-colors"
                        onClick={e => e.stopPropagation()}
                      >
                        利用規約
                      </a>
                      および
                      <a
                        href="/sitter-terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#81D8D0] underline hover:text-[#5bc0b8] transition-colors"
                        onClick={e => e.stopPropagation()}
                      >
                        シッター利用規約
                      </a>
                      に同意します
                    </span>
                  ),
                },
                { key: 'platform', label: 'PawBridgeはペットオーナーとシッターをつなぐマッチングプラットフォームであり、PawBridge自身がペットケアサービスを提供するものではないことを理解しています' },
                { key: 'liability', label: 'ペットケア中の行為、事故、トラブルに関する責任は、法令上認められる範囲でシッター自身にも生じることを理解しています' },
                { key: 'independent', label: '私は、PawBridgeの従業員ではなく、独立したサービス提供者として活動することを理解しています' },
                { key: 'tax', label: 'PawBridgeを通じて得た収入について、自らの責任で各国の税法に従い申告・納税する必要があることを理解しています' },
                { key: 'insurance', label: 'ペットケア中の事故や損害に備え、賠償責任保険への加入が推奨されていることを理解しています' },
                { key: 'accuracy', label: '提出した情報が正確かつ最新であることを確認します' },
                { key: 'pending', label: '審査完了前はシッターとして公開されないことを理解しています' },
                { key: 'noDirectDeal', label: 'PawBridgeを通じて知り合った利用者と、プラットフォーム外で直接契約・直接決済を行わないことに同意します' },
              ].map((item) => (
                <label key={item.key} className={`flex items-start gap-3 p-4 border rounded-lg transition-colors cursor-pointer ${
                  agreementError && !formData.agreements[item.key as keyof typeof formData.agreements]
                    ? 'border-red-300 bg-red-50/50 hover:bg-red-50'
                    : 'border-[#D4E8EC] hover:border-[#81D8D0]/40 hover:bg-[#EEF4F7]/50'
                }`}>
                  <input
                    type="checkbox"
                    name={`agreements.${item.key}`}
                    checked={formData.agreements[item.key as keyof typeof formData.agreements]}
                    onChange={(e) => {
                      handleCheckboxChange(e);
                      if (agreementError) setAgreementError(false);
                    }}
                    className="w-5 h-5 text-[#81D8D0] border-[#D4E8EC] rounded focus:ring-[#81D8D0] mt-0.5 flex-shrink-0"
                    required
                  />
                  <span className="text-sm text-[#1B2A4A] leading-relaxed">{item.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* 送信ボタン */}
          <div className="text-center">
            <button
              type="submit"
              className="px-12 py-4 bg-gradient-to-r from-[#81D8D0] to-[#5BC0B8] text-white text-lg font-medium rounded-full hover:shadow-xl transition-all whitespace-nowrap"
            >
              シッター応募を送信する
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}